/*
  Devora House — search enhancements on top of the Parfum theme LiveSearch module.

  The theme owns open/close, focus trapping, Escape, close-on-outside-click and the
  fetch of predictive results. Only one of the two header search instances keeps
  [data-live-search] (see snippets/header-minimal.liquid), so the theme binds to the
  instance that matches the current breakpoint.

  This file adds three things the theme has no setting for:
    1. Default brand suggestions while the field is still empty.
    2. A shorter predictive-search debounce than the theme's 500 ms.
    3. Layout for the full-width results sheet, neutralising the inline styles the
       theme writes on the results container every frame.
*/
(function () {
  'use strict';

  var MOBILE_MQ = window.matchMedia('(max-width: 719px)');
  var PREDICTIVE_DEBOUNCE_MS = 180;
  var FALLBACK_SEARCH_GAP_PX = 13.6;
  var INLINE_LAYOUT_PROPS = ['position', 'top', 'left', 'right', 'bottom', 'transform', 'width', 'padding-bottom'];

  var resultsObserver = null;
  var rootClassObserver = null;
  var layoutTimer = null;
  var predictiveTimer = null;
  var predictiveAbort = null;
  var searchGapPx = null;
  var defaultSuggestionsHtml = null;
  var openDesktopSearch = null;

  function isMobile() {
    return MOBILE_MQ.matches;
  }

  function isOpen() {
    return document.documentElement.classList.contains('live-search-visible');
  }

  /* The instance the theme bound to: the only one that kept [data-live-search]. */
  function getRoot() {
    return document.querySelector('.main-header [data-live-search]');
  }

  function getDesktopScope() {
    return document.querySelector('.devora-header-desktop__search');
  }

  function inRoot(selector) {
    var root = getRoot();
    return root ? root.querySelector(selector) : null;
  }

  function getSearchGapPx() {
    if (searchGapPx !== null) return searchGapPx;
    var search = document.querySelector('.devora-header-unified__search');
    if (!search) return FALLBACK_SEARCH_GAP_PX;
    var gap = parseFloat(getComputedStyle(search).getPropertyValue('--devora-search-bar-gap'));
    searchGapPx = Number.isFinite(gap) ? gap : FALLBACK_SEARCH_GAP_PX;
    return searchGapPx;
  }

  function bottomWithMargin(el) {
    if (!el) return 0;
    var rect = el.getBoundingClientRect();
    var marginBottom = parseFloat(window.getComputedStyle(el).marginBottom) || 0;
    return rect.bottom + marginBottom;
  }

  function setSheetTop(value) {
    document.documentElement.style.setProperty('--devora-search-sheet-top', value + 'px');
  }

  /*
    Pin the sheet before the theme animates the panel in, so it does not flash at
    the theme's default position on the first frame.
  */
  function presetSheetTopFromPill(pill) {
    if (!pill) return;
    var offset = isMobile() ? getSearchGapPx() : 0;
    setSheetTop(pill.getBoundingClientRect().bottom + offset);
  }

  /*
    On mobile the results sheet flows under the header bar, so the inline styles the
    theme writes on the form and results container every frame have to be forced back
    to a neutral flow. Desktop keeps the panel out of flow and is handled in CSS,
    where `!important` already wins over the theme's inline values.
  */
  function lockSearchLayout() {
    if (!isMobile()) return;

    var form = inRoot('[data-live-search-form]');
    var results = inRoot('[data-live-search-results]');
    var predictive = inRoot('[data-predictive-search]');

    if (form) {
      form.style.setProperty('position', 'relative', 'important');
      ['top', 'left', 'right'].forEach(function (prop) {
        form.style.setProperty(prop, 'auto', 'important');
      });
      form.style.setProperty('transform', 'none', 'important');
      form.style.setProperty('width', '100%', 'important');
    }

    if (results) {
      results.style.setProperty('position', 'relative', 'important');
      ['top', 'left', 'right', 'bottom'].forEach(function (prop) {
        results.style.setProperty(prop, 'auto', 'important');
      });
      results.style.setProperty('padding-bottom', '0', 'important');
    }

    if (predictive) predictive.style.setProperty('padding-bottom', '0', 'important');
  }

  function unlockSearchLayout() {
    [inRoot('[data-live-search-form]'), inRoot('[data-live-search-results]'), inRoot('[data-predictive-search]')].forEach(
      function (el) {
        if (!el) return;
        INLINE_LAYOUT_PROPS.forEach(function (prop) {
          el.style.removeProperty(prop);
        });
      }
    );
  }

  function syncSearchLayout() {
    if (!isOpen()) return;

    lockSearchLayout();

    var root = getRoot();
    var results = inRoot('[data-live-search-results]');
    var form = inRoot('[data-live-search-form]');
    var resultsHidden = root && root.classList.contains('live-search-results-hidden');

    if (results && results.children.length > 0 && !resultsHidden) {
      setSheetTop(results.getBoundingClientRect().top);
    } else if (form) {
      setSheetTop(bottomWithMargin(form));
    }
  }

  function scheduleSyncSearchLayout() {
    clearTimeout(layoutTimer);
    layoutTimer = setTimeout(syncSearchLayout, 0);
  }

  function getDefaultSuggestionsHtml() {
    if (defaultSuggestionsHtml !== null) return defaultSuggestionsHtml;
    var tpl = document.getElementById('devora-search-defaults-template');
    if (!tpl) return '';
    var node = tpl.content && tpl.content.firstElementChild;
    defaultSuggestionsHtml = node ? node.outerHTML : tpl.innerHTML;
    return defaultSuggestionsHtml;
  }

  function showDefaultSuggestions() {
    var root = getRoot();
    var results = inRoot('[data-live-search-results]');
    var html = getDefaultSuggestionsHtml();
    if (!root || !results || !html) return;

    results.innerHTML = html;
    eagerLoadSearchImages(results);
    root.classList.remove('live-search-results-hidden');
    syncSearchLayout();
    scheduleSyncSearchLayout();
  }

  function parsePredictiveHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var block = doc.querySelector('[data-html]');
    return block ? block.innerHTML : html;
  }

  function eagerLoadSearchImages(container) {
    if (!container) return;

    container.querySelectorAll('img[data-rimg="lazy"]').forEach(function (img) {
      var noscript = img.previousElementSibling;
      var source = noscript && noscript.querySelector('img');

      if (source && source.getAttribute('src')) {
        img.src = source.getAttribute('src');
        var srcset = source.getAttribute('srcset');
        if (srcset) img.srcset = srcset;
      } else {
        var template = img.getAttribute('data-rimg-template');
        if (template) img.src = template.replace('_{size}', '_120x120');
      }

      img.setAttribute('data-rimg', 'loaded');
      img.removeAttribute('data-rimg-template');
    });

    container.querySelectorAll('img.devora-predictive-thumb[loading="lazy"]').forEach(function (img) {
      img.loading = 'eager';
    });
  }

  function runPredictiveSearch(query) {
    var root = getRoot();
    var results = inRoot('[data-live-search-results]');
    if (!root || !results || !window.Theme || !window.Theme.routes) return;

    if (query.length < 1) {
      showDefaultSuggestions();
      return;
    }

    root.classList.remove('live-search-results-hidden');

    if (predictiveAbort) predictiveAbort.abort();
    predictiveAbort = new AbortController();

    var url =
      window.Theme.routes.predictive_search_url +
      '?q=' +
      encodeURIComponent(query) +
      '&section_id=predictive-search';

    fetch(url, { signal: predictiveAbort.signal })
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        var input = inRoot('[data-live-search-form-input]');
        if (!input || input.value !== query) return;
        results.innerHTML = parsePredictiveHtml(html);
        eagerLoadSearchImages(results);
        syncSearchLayout();
        scheduleSyncSearchLayout();
      })
      .catch(function () {});
  }

  /*
    Swallow the keyup the theme listens to so its 500 ms debounce never runs, and
    replace it with a faster one.
  */
  function bindFastPredictiveSearch(root) {
    var input = root && root.querySelector('[data-live-search-form-input]');
    if (!input || input.dataset.devoraFastSearchBound) return;
    input.dataset.devoraFastSearchBound = 'true';

    input.addEventListener(
      'keyup',
      function (e) {
        if (isOpen()) e.stopImmediatePropagation();
      },
      true
    );

    input.addEventListener('input', function () {
      if (openDesktopSearch) openDesktopSearch();
      if (!isOpen()) return;
      clearTimeout(predictiveTimer);
      var query = input.value;
      predictiveTimer = setTimeout(function () {
        runPredictiveSearch(query);
      }, PREDICTIVE_DEBOUNCE_MS);
    });
  }

  function bindObservers() {
    var root = getRoot();
    var results = root && root.querySelector('[data-live-search-results]');
    if (!root || !results || root.dataset.devoraSearchObserved) return;
    root.dataset.devoraSearchObserved = 'true';

    resultsObserver = new MutationObserver(scheduleSyncSearchLayout);
    resultsObserver.observe(results, { childList: true });

    rootClassObserver = new MutationObserver(syncSearchLayout);
    rootClassObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
  }

  function unbindObservers() {
    [resultsObserver, rootClassObserver].forEach(function (observer) {
      if (observer) observer.disconnect();
    });
    resultsObserver = null;
    rootClassObserver = null;

    var root = getRoot();
    if (root) delete root.dataset.devoraSearchObserved;
  }

  function onSearchOpen() {
    if (!getRoot()) return;
    bindObservers();
    bindFastPredictiveSearch(getRoot());
    showDefaultSuggestions();
    syncSearchLayout();
    requestAnimationFrame(syncSearchLayout);
  }

  function onSearchClose() {
    unbindObservers();
    clearTimeout(layoutTimer);
    clearTimeout(predictiveTimer);
    if (predictiveAbort) predictiveAbort.abort();
    unlockSearchLayout();
    document.documentElement.style.removeProperty('--devora-search-sheet-top');
    keepDesktopFieldTabbable();
  }

  /*
    The theme keeps the input at tabindex -1 while the panel is closed because it
    normally lives behind a pill. On desktop the field is permanently visible, so
    it has to stay reachable by keyboard.
  */
  function keepDesktopFieldTabbable() {
    if (isMobile()) return;
    var input = getDesktopScope() && getDesktopScope().querySelector('[data-live-search-form-input]');
    if (input) input.tabIndex = 0;
  }

  /*
    Desktop shows the field permanently (Fandi layout) while the theme only opens
    on its search pill, which is hidden there. Interacting with the field clicks
    that pill so the theme's own open/close behaviour stays intact.
  */
  function initDesktopOpenBridge() {
    var scope = getDesktopScope();
    var root = scope && scope.querySelector('[data-live-search]');
    if (!root || scope.dataset.devoraOpenBridge) return;

    var form = root.querySelector('[data-live-search-form]');
    var input = root.querySelector('[data-live-search-form-input]');
    var openButton = root.querySelector('[data-live-search-button-search]');
    if (!form || !input || !openButton) return;
    scope.dataset.devoraOpenBridge = 'true';
    keepDesktopFieldTabbable();

    openDesktopSearch = function () {
      if (isMobile() || isOpen()) return;
      presetSheetTopFromPill(form);
      openButton.click();
    };

    form.addEventListener('mousedown', function (e) {
      openDesktopSearch();
      if (e.target === input) return;
      e.preventDefault();
      input.focus();
    });

    input.addEventListener('focus', openDesktopSearch);
  }

  function initPillPreset() {
    document.addEventListener(
      'click',
      function (e) {
        var pill = e.target.closest('[data-live-search-button-search]');
        if (pill) presetSheetTopFromPill(pill);
      },
      true
    );
  }

  function initStateObserver() {
    if (document.documentElement.dataset.devoraSearchStateBound) return;
    document.documentElement.dataset.devoraSearchStateBound = 'true';

    new MutationObserver(function () {
      if (isOpen()) {
        onSearchOpen();
      } else {
        onSearchClose();
      }
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    initPillPreset();
    initStateObserver();
    initDesktopOpenBridge();
    bindFastPredictiveSearch(getRoot());

    /*
      The theme instantiates LiveSearch after the jQuery API bundle loads, so the
      bridge is retried once everything is on the page.
    */
    window.addEventListener('load', initDesktopOpenBridge);

    window.addEventListener('resize', function () {
      searchGapPx = null;
      if (isOpen()) syncSearchLayout();
    });

    if (isOpen()) onSearchOpen();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
